import {
  PhCalculationRequest,
  PhCalculationResponseDTO,
  PhCalculationWithHistoryRequest,
  PhCalculationWithHistoryResponseDTO
} from "@henophess/types";

// Interface for saving pH data with member ID
interface SavePhDataRequest {
  calculationRequest: PhCalculationRequest;
  groupMemberId: number;
}

// Extended type for response that might include duplicate flag or auth requirements
interface PhResponseWithDuplicate extends PhCalculationResponseDTO {
  duplicate?: boolean;
  message?: string;
  requiresAuth?: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

export const submitPhCalculation = async (data: PhCalculationRequest): Promise<PhCalculationResponseDTO> => {
  const response = await fetch(`${API_BASE_URL}/calculations/analyze-ph`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data),
    // Ensure cookies are sent with the request (Clerk uses cookies)
    credentials: "include"
  });

  if (!response.ok) throw new Error("Failed to analyze pH");
  return response.json();
};

export const submitPhCalculationWithHistory = async (
  data: PhCalculationWithHistoryRequest
): Promise<PhCalculationWithHistoryResponseDTO> => {
  const response = await fetch(`${API_BASE_URL}/calculations/analyze-ph-history`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data),
    credentials: "include"
  });

  if (!response.ok) throw new Error("Failed to analyze pH with history");
  return response.json();
};

// Custom error class for authentication errors
export class AuthenticationRequiredError extends Error {
  constructor(message: string = "Please log in to save your pH data") {
    super(message);
    this.name = "AuthenticationRequiredError";
  }
}

export const savePhData = async (
  calculationRequest: PhCalculationRequest,
  groupMemberId: number
): Promise<PhResponseWithDuplicate> => {
  const saveRequest: SavePhDataRequest = {
    calculationRequest,
    groupMemberId
  };

  const response = await fetch(`${API_BASE_URL}/calculations/save-ph-data`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(saveRequest),
    credentials: "include"
  });

  // Handle the response based on status code
  if (!response.ok) {
    const responseData = await response.json().catch(() => ({ message: "Unknown error" }));

    // If it's a 401 unauthorized, throw a specific error for authentication
    if (response.status === 401) {
      throw new AuthenticationRequiredError(responseData.message || "Please log in to save your pH data");
    }

    // For other errors, throw a generic error
    throw new Error(responseData.message || "Failed to save pH data");
  }

  return response.json();
};

// Interface for pH history entries
export interface PhHistoryEntry {
  id: number;
  memberId: number; // Keep for backward compatibility
  groupMemberId: number;
  salivaPh: number;
  urinePh: number;
  createdAt: string;
  testingDate: string;
}

// Interface for pH history response
export interface PhHistoryResponse {
  success: boolean;
  data?: PhHistoryEntry[];
  requiresAuth?: boolean;
  message?: string;
  error?: string;
}

/**
 * Fetch pH history for the current user
 * @param groupMemberId The group member ID to fetch history for
 */
export const fetchPhHistory = async (groupMemberId: number): Promise<PhHistoryEntry[]> => {
  const response = await fetch(`${API_BASE_URL}/ph-history?groupMemberId=${groupMemberId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include"
  });

  // Handle the response based on status code
  if (!response.ok) {
    const responseData = await response.json().catch(() => ({ message: "Unknown error" }));

    // If it's a 401 unauthorized, throw a specific error for authentication
    if (response.status === 401) {
      throw new AuthenticationRequiredError(responseData.message || "Please log in to view your pH history");
    }

    // For other errors, throw a generic error
    throw new Error(responseData.message || "Failed to fetch pH history");
  }

  const responseData = (await response.json()) as PhHistoryResponse;
  return responseData.data || [];
};

/**
 * Delete a pH analysis entry
 * @param id The pH analysis entry ID
 * @param groupMemberId The group member ID
 */
export const deletePhAnalysis = async (
  id: number,
  groupMemberId: number
): Promise<{ success: boolean; message?: string }> => {
  const response = await fetch(`${API_BASE_URL}/ph_analysis/${id}?groupMemberId=${groupMemberId}`, {
    method: "DELETE",
    // Don't include Content-Type header for DELETE requests with no body
    credentials: "include"
  });

  // Handle the response based on status code
  if (!response.ok) {
    const responseData = await response.json().catch(() => ({ message: "Unknown error" }));

    // If it's a 401 unauthorized, throw a specific error for authentication
    if (response.status === 401) {
      throw new AuthenticationRequiredError(responseData.message || "Please log in to delete pH analysis entries");
    }

    // For other errors, throw a generic error
    throw new Error(responseData.message || "Failed to delete pH analysis entry");
  }

  return await response.json();
};
